/**
 * financialsEngine.js — Pure aggregation functions for the Financials tab.
 *
 * All functions are side-effect-free. Consume job rows, invoice rows,
 * expense rows, and date ranges — return structured numbers.
 */

import { normalizeSnapshot, getAllEarnings } from './calcEngine';
import { getStaticCharges, paymentDate } from './utils';

// ─── Status buckets ────────────────────────────────────────────────────
const PIPELINE_STATUSES = ['intake', 'quoted'];
const BOOKED_STATUSES = ['confirmed', 'shooting', 'post_production', 'partial_delivery'];
const EARNED_STATUSES = ['delivered', 'invoiced', 'paid'];
// Anything that has been confirmed (moved past pipeline & is not void/archived)
const CONFIRMED_STATUSES = [...BOOKED_STATUSES, ...EARNED_STATUSES];

// ─── Date helpers ──────────────────────────────────────────────────────

function toDateStr(v) {
  if (!v) return '';
  if (typeof v === 'string') return v.length >= 10 ? v.slice(0, 10) : '';
  try { return new Date(v).toISOString().slice(0, 10); } catch { return ''; }
}

/** When a paid invoice was paid: paid_at, else its latest payment, else when it was sent. */
export function invoicePaidDate(inv) {
  return toDateStr(inv.paidAt) ||
    (inv.payments || []).reduce((latest, p) => {
      const d = paymentDate(p);
      return d > latest ? d : latest;
    }, '') ||
    toDateStr(inv.sentAt);
}

/**
 * Open balance past its due date. Nothing stores an 'overdue' status; it's derived.
 * `today` is the caller's local 'YYYY-MM-DD'.
 */
export function isInvoiceOverdue(inv, today) {
  if (!['unpaid', 'sent', 'partial'].includes(inv.status)) return false;
  const balance = (parseFloat(inv.amount) || 0) - (parseFloat(inv.paidAmount) || 0);
  const due = toDateStr(inv.dueDate);
  return balance > 0 && !!due && due < today;
}

function inRange(dateStr, start, end) {
  if (!dateStr) return false;
  if (start && dateStr < start) return false;
  if (end && dateStr > end) return false;
  return true;
}

function daysBetween(a, b) {
  const da = new Date(a + 'T12:00');
  const db = new Date(b + 'T12:00');
  return Math.round((db - da) / 86400000);
}

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

// ─── Per-bucket date attribution ───────────────────────────────────────

/** Pipeline date = when the lead/quote was created. */
function pipelineDate(job) { return toDateStr(job.createdAt); }
/** Booked date = shootDate if scheduled, else when created. */
function bookedDate(job) { return job.shootDate || toDateStr(job.createdAt); }
/** Earned date = deliveryDate if known, else shootDate, else createdAt. */
function earnedDate(job) { return job.deliveryDate || job.shootDate || toDateStr(job.createdAt); }
/** Invoice issued date = sentAt, else createdAt. */
function invoiceIssuedDate(inv) { return toDateStr(inv.sentAt) || toDateStr(inv.createdAt); }

// ─── Revenue funnel ────────────────────────────────────────────────────

/**
 * Five revenue buckets, each filtered by the date field that makes sense
 * for its lifecycle stage.
 *
 * @returns {{
 *   pipeline: { total, jobCount },
 *   booked:   { total, jobCount },
 *   earned:   { total, jobCount },
 *   invoiced: { total, invoiceCount },
 *   collected:{ total, paymentCount },
 *   outstanding: number
 * }}
 */
export function computeRevenueBuckets(jobs, invoices, startDate, endDate) {
  const active = (jobs || []).filter(j => j.status !== 'archived' && j.status !== 'void');
  const validInvoices = (invoices || []).filter(inv => inv.status !== 'void');

  // Build a map: jobId -> { hasPaidInvoice, anyInvoice, latestPaidDate }
  // so we can treat a confirmed job with a fully-paid invoice as "earned"
  // (handles the common case where job.status isn't advanced when invoices paid).
  const jobInvoiceMeta = {};
  validInvoices.forEach(inv => {
    // A bulk invoice covers several jobs via jobIds (jobId is null) — every
    // covered job counts as invoiced/paid, not just the FK one.
    const coveredIds = [inv.jobId, ...(Array.isArray(inv.jobIds) ? inv.jobIds : [])].filter(Boolean);
    if (!coveredIds.length) return;
    const paidDate = inv.status === 'paid' ? invoicePaidDate(inv) : '';
    coveredIds.forEach(jid => {
      const meta = jobInvoiceMeta[jid] || { anyInvoice: false, hasPaidInvoice: false, latestPaidDate: '' };
      meta.anyInvoice = true;
      if (inv.status === 'paid') {
        meta.hasPaidInvoice = true;
        if (paidDate > meta.latestPaidDate) meta.latestPaidDate = paidDate;
      }
      jobInvoiceMeta[jid] = meta;
    });
  });

  const isStatusEarned = (j) => EARNED_STATUSES.includes(j.status);
  const isInvoiceEarned = (j) => !!jobInvoiceMeta[j.id]?.hasPaidInvoice;
  const isEarned = (j) => isStatusEarned(j) || isInvoiceEarned(j);

  // Earned-date: prefer paid-invoice date when status lags; fall back to deliveryDate/shootDate/createdAt
  const earnedDateFor = (j) => {
    const paidDate = jobInvoiceMeta[j.id]?.latestPaidDate;
    if (paidDate) return paidDate;
    return earnedDate(j);
  };

  const pipelineJobs = active.filter(j => PIPELINE_STATUSES.includes(j.status) && inRange(pipelineDate(j), startDate, endDate));
  // Booked excludes jobs that have already been "earned" (via status or paid invoice)
  const bookedJobs = active.filter(j =>
    BOOKED_STATUSES.includes(j.status) && !isEarned(j) && inRange(bookedDate(j), startDate, endDate),
  );
  const earnedJobs = active.filter(j => isEarned(j) && inRange(earnedDateFor(j), startDate, endDate));

  const sumRev = (arr) => arr.reduce((s, j) => s + (parseFloat(j.revenue) || 0), 0);

  // Invoiced: by invoice issue date
  const invoicedInPeriod = validInvoices.filter(inv => inRange(invoiceIssuedDate(inv), startDate, endDate));
  const invoicedTotal = invoicedInPeriod.reduce((s, inv) => s + (parseFloat(inv.amount) || 0), 0);

  // Collected: by payment date (across all non-void invoices)
  let collectedTotal = 0;
  let paymentCount = 0;
  validInvoices.forEach(inv => {
    (inv.payments || []).forEach(p => {
      if (p.voided) return;
      const d = paymentDate(p);
      if (!inRange(d, startDate, endDate)) return;
      collectedTotal += parseFloat(p.amount) || 0;
      paymentCount += 1;
    });
  });

  // Outstanding A/R (not period-scoped — it's a current snapshot)
  const outstanding = validInvoices.reduce(
    (s, inv) => s + ((parseFloat(inv.amount) || 0) - (parseFloat(inv.paidAmount) || 0)),
    0,
  );

  return {
    pipeline:  { total: sumRev(pipelineJobs), jobCount: pipelineJobs.length, jobs: pipelineJobs },
    booked:    { total: sumRev(bookedJobs),   jobCount: bookedJobs.length,   jobs: bookedJobs },
    earned:    { total: sumRev(earnedJobs),   jobCount: earnedJobs.length,   jobs: earnedJobs },
    invoiced:  { total: invoicedTotal, invoiceCount: invoicedInPeriod.length },
    collected: { total: collectedTotal, paymentCount },
    outstanding,
  };
}

// ─── Simplified P&L ────────────────────────────────────────────────────

/**
 * Revenue = Earned (delivered + invoiced + paid, by delivery date).
 * COGS = crew costs (from calcSnapshots on earned jobs) + variable + recurring expenses.
 * Net margin = (revenue - COGS) / revenue.
 */
export function computePL(jobs, invoices, expenses, staticExpenses, startDate, endDate) {
  const buckets = computeRevenueBuckets(jobs, invoices, startDate, endDate);
  const earnedJobs = buckets.earned.jobs;
  const revenue = buckets.earned.total;

  // Crew costs from earned jobs' snapshots
  let totalCrewCost = 0;
  const crewCosts = {};
  earnedJobs.forEach(j => {
    const ns = normalizeSnapshot(j.calcSnapshot);
    getAllEarnings(ns).forEach(e => {
      totalCrewCost += e.earned;
      crewCosts[e.name] = (crewCosts[e.name] || 0) + e.earned;
    });
  });

  // Variable expenses in period
  const varExpenses = (expenses || [])
    .filter(e => inRange(toDateStr(e.date || e.createdAt), startDate, endDate))
    .reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);

  // Recurring / static expenses prorated into period
  let recurringExpenses = 0;
  if (staticExpenses && startDate && endDate) {
    const sYear = parseInt(startDate.slice(0, 4));
    const eYear = parseInt(endDate.slice(0, 4));
    for (let y = sYear; y <= eYear; y++) {
      staticExpenses.forEach(se => {
        getStaticCharges(se, y).forEach(({ monthKey, amount }) => {
          const mk = `${monthKey}-01`;
          if (inRange(mk, startDate, endDate)) recurringExpenses += amount;
        });
      });
    }
  }

  const totalExpenses = varExpenses + recurringExpenses;
  const cogs = totalCrewCost + totalExpenses;
  const grossProfit = revenue - cogs;
  const netMarginPct = revenue > 0 ? (grossProfit / revenue) * 100 : 0;

  return {
    revenue,
    jobCount: earnedJobs.length,
    totalCrewCost,
    crewCosts,
    varExpenses,
    recurringExpenses,
    totalExpenses,
    cogs,
    grossProfit,
    netMarginPct,
  };
}

// ─── Cash Position ─────────────────────────────────────────────────────

/**
 * Returns current A/R health as a snapshot (not period-scoped).
 *  - outstanding: sum of unpaid balances on all non-void invoices
 *  - overdue:     portion of outstanding where due date is >60 days past
 *  - expected30:  portion that's due within the next 30 days (or already due
 *                 but ≤60 days overdue — still "expected")
 */
export function computeCashPosition(invoices) {
  const today = todayStr();
  let outstanding = 0;
  let overdue = 0;
  let expected30 = 0;

  (invoices || []).forEach(inv => {
    if (inv.status === 'void') return;
    const balance = (parseFloat(inv.amount) || 0) - (parseFloat(inv.paidAmount) || 0);
    if (balance <= 0) return;

    outstanding += balance;

    const due = toDateStr(inv.dueDate);
    if (!due) {
      expected30 += balance;
      return;
    }

    const daysLate = daysBetween(due, today);
    if (daysLate > 60) {
      overdue += balance;
    } else {
      // due within 30 days (past or future) — count as expected
      expected30 += balance;
    }
  });

  return { outstanding, overdue, expected30 };
}

// ─── Action Items ──────────────────────────────────────────────────────

/**
 * Things that need attention — rendered as action cards.
 *  - deliveredNotInvoiced: jobs with status='delivered' where no non-void invoice exists
 *  - overdueInvoices: non-void invoices >60 days past due with balance > 0
 *  - voidedJobs: jobs voided within period (by updatedAt)
 */
export function computeActionItems(jobs, invoices, startDate, endDate) {
  const today = todayStr();
  const active = (jobs || []).filter(j => j.status !== 'archived');
  const validInvoices = (invoices || []).filter(inv => inv.status !== 'void');

  // Jobs delivered but without a non-void invoice (bulk invoices cover their
  // jobs via jobIds, not jobId)
  const invoicedJobIds = new Set(
    validInvoices.flatMap(inv => [inv.jobId, ...(Array.isArray(inv.jobIds) ? inv.jobIds : [])]).filter(Boolean)
  );
  const deliveredNotInvoiced = active.filter(j => {
    if (j.status !== 'delivered') return false;
    return !invoicedJobIds.has(j.id);
  });
  const deliveredNotInvoicedTotal = deliveredNotInvoiced.reduce(
    (s, j) => s + (parseFloat(j.revenue) || 0), 0,
  );

  // Overdue invoices >60 days
  const overdueInvoices = validInvoices.filter(inv => {
    const balance = (parseFloat(inv.amount) || 0) - (parseFloat(inv.paidAmount) || 0);
    if (balance <= 0) return false;
    const due = toDateStr(inv.dueDate);
    if (!due) return false;
    return daysBetween(due, today) > 60;
  });
  const overdueTotal = overdueInvoices.reduce(
    (s, inv) => s + ((parseFloat(inv.amount) || 0) - (parseFloat(inv.paidAmount) || 0)), 0,
  );

  // Voided jobs in period (by updatedAt as proxy for void date)
  const voidedInPeriod = (jobs || []).filter(j => {
    if (j.status !== 'void') return false;
    const d = toDateStr(j.updatedAt || j.createdAt);
    return inRange(d, startDate, endDate);
  });
  const voidedTotal = voidedInPeriod.reduce(
    (s, j) => s + (parseFloat(j.revenue) || 0), 0,
  );

  return {
    deliveredNotInvoiced,
    deliveredNotInvoicedTotal,
    overdueInvoices,
    overdueTotal,
    voidedJobs: voidedInPeriod,
    voidedTotal,
  };
}

// ─── Business Health (rolling 12mo, NOT period-scoped) ─────────────────

/**
 * Stale-quote cutoff: quotes still in `quoted` status older than this many
 * days count against conversion rate.
 */
const STALE_QUOTE_DAYS = 60;

/**
 * Conversion rate:
 *   won      = quotes that ever reached confirmed+ status
 *   lost     = voided quotes (jobs voided while in quoted/intake status)
 *              NOTE: we approximate "voided quotes" as any void job
 *   stale    = still in `quoted` status for >60 days
 *   rate     = won / (won + lost + stale)
 *
 * Rolling 12 months by createdAt.
 */
export function computeBusinessHealth(jobs) {
  const today = new Date();
  const cutoff = new Date(today);
  cutoff.setMonth(cutoff.getMonth() - 12);
  const cutoffStr = cutoff.toISOString().slice(0, 10);
  const todayISO = today.toISOString().slice(0, 10);

  const recentJobs = (jobs || []).filter(j => {
    const d = toDateStr(j.createdAt);
    return d && d >= cutoffStr;
  });

  const won = recentJobs.filter(j => CONFIRMED_STATUSES.includes(j.status)).length;
  const lost = recentJobs.filter(j => j.status === 'void').length;
  const stale = recentJobs.filter(j => {
    if (!['quoted', 'intake'].includes(j.status)) return false;
    const d = toDateStr(j.createdAt);
    return daysBetween(d, todayISO) > STALE_QUOTE_DAYS;
  }).length;
  const openQuotes = recentJobs.filter(j => {
    if (!['quoted', 'intake'].includes(j.status)) return false;
    const d = toDateStr(j.createdAt);
    return daysBetween(d, todayISO) <= STALE_QUOTE_DAYS;
  }).length;

  const denom = won + lost + stale;
  const conversionPct = denom > 0 ? (won / denom) * 100 : 0;

  // Avg turnaround: shoot → delivery for delivered+ jobs in last 12mo
  const turnarounds = recentJobs
    .filter(j => j.shootDate && j.deliveryDate && EARNED_STATUSES.includes(j.status))
    .map(j => daysBetween(j.shootDate, j.deliveryDate))
    .filter(d => d > 0);
  const avgTurnaroundDays = turnarounds.length
    ? Math.round(turnarounds.reduce((s, d) => s + d, 0) / turnarounds.length)
    : 0;

  // Retention: clients with >1 job in last 12mo, out of all distinct clients
  const clientCounts = {};
  recentJobs.forEach(j => {
    const c = j.clientName || j.contactName;
    if (c) clientCounts[c] = (clientCounts[c] || 0) + 1;
  });
  const totalClients = Object.keys(clientCounts).length;
  const repeatClients = Object.values(clientCounts).filter(c => c > 1).length;
  const retentionPct = totalClients > 0 ? (repeatClients / totalClients) * 100 : 0;

  return {
    conversionPct, won, lost, stale, openQuotes,
    avgTurnaroundDays, turnaroundSampleSize: turnarounds.length,
    retentionPct, totalClients, repeatClients,
  };
}

// ─── Monthly Grid ──────────────────────────────────────────────────────

/**
 * 12-month year grid of revenue / collected / expenses / payroll.
 * Revenue uses earned-date so it reflects actual work done per month.
 *
 * @param {Array} payPeriodPayments - rows from pay_period_payments table
 *   (each row has `payments: [{ amount, date, method, ... }]`). Payroll is
 *   attributed to the month of each individual payment's date.
 */
export function computeMonthlyGrid(jobs, invoices, expenses, staticExpenses, payPeriodPayments, year) {
  const months = {};
  for (let m = 1; m <= 12; m++) {
    const key = `${year}-${String(m).padStart(2, '0')}`;
    months[key] = { revenue: 0, collected: 0, expenses: 0, payroll: 0 };
  }

  const monthKey = (dateStr) => {
    if (!dateStr) return null;
    const d = toDateStr(dateStr);
    if (!d) return null;
    return d.slice(0, 7);
  };

  // Revenue by earned-date; include jobs earned via status OR via a paid invoice.
  const validInvoices = (invoices || []).filter(inv => inv.status !== 'void');
  const paidInvoiceByJob = {};
  validInvoices.forEach(inv => {
    if (inv.status !== 'paid') return;
    const coveredIds = [inv.jobId, ...(Array.isArray(inv.jobIds) ? inv.jobIds : [])].filter(Boolean);
    if (!coveredIds.length) return;
    const paidDate = invoicePaidDate(inv);
    coveredIds.forEach(jid => {
      const prev = paidInvoiceByJob[jid];
      if (!prev || paidDate > prev) paidInvoiceByJob[jid] = paidDate;
    });
  });

  (jobs || []).forEach(j => {
    if (j.status === 'void' || j.status === 'archived') return;
    const hasPaidInvoice = !!paidInvoiceByJob[j.id];
    const statusEarned = EARNED_STATUSES.includes(j.status);
    if (!statusEarned && !hasPaidInvoice) return;
    const effDate = paidInvoiceByJob[j.id] || earnedDate(j);
    const mk = monthKey(effDate);
    if (mk && months[mk]) months[mk].revenue += parseFloat(j.revenue) || 0;
  });

  // Collected: by payment date
  (invoices || []).forEach(inv => {
    if (inv.status === 'void') return;
    (inv.payments || []).forEach(p => {
      if (p.voided) return;
      const mk = monthKey(paymentDate(p));
      if (mk && months[mk]) months[mk].collected += parseFloat(p.amount) || 0;
    });
  });

  // Variable expenses
  (expenses || []).forEach(exp => {
    const mk = monthKey(exp.date || exp.createdAt);
    if (mk && months[mk]) months[mk].expenses += parseFloat(exp.amount) || 0;
  });

  // Recurring expenses prorated
  (staticExpenses || []).forEach(se => {
    getStaticCharges(se, year).forEach(({ monthKey: mk, amount }) => {
      if (months[mk]) months[mk].expenses += amount;
    });
  });

  // Payroll: sum each individual payment entry by its date's month
  (payPeriodPayments || []).forEach(row => {
    (row.payments || []).forEach(p => {
      if (p.voided) return;
      const mk = monthKey(p.date);
      if (mk && months[mk]) months[mk].payroll += parseFloat(p.amount) || 0;
    });
  });

  const totals = { revenue: 0, collected: 0, expenses: 0, payroll: 0 };
  Object.values(months).forEach(m => {
    totals.revenue += m.revenue;
    totals.collected += m.collected;
    totals.expenses += m.expenses;
    totals.payroll += m.payroll;
  });

  return { months, totals };
}

// ─── Revenue by Type + Top Clients (uses Earned) ───────────────────────

/**
 * Segmentation uses the Earned bucket — real revenue only.
 */
export function computeSegments(earnedJobs) {
  const byType = {};
  (earnedJobs || []).forEach(j => {
    const t = j.jobType || 'Other';
    if (!byType[t]) byType[t] = { count: 0, rev: 0 };
    byType[t].count++;
    byType[t].rev += parseFloat(j.revenue) || 0;
  });
  const typesSorted = Object.entries(byType).sort((a, b) => b[1].rev - a[1].rev);

  const clientRev = {};
  (earnedJobs || []).forEach(j => {
    const c = j.clientName || j.contactName;
    if (c) clientRev[c] = (clientRev[c] || 0) + (parseFloat(j.revenue) || 0);
  });
  const topClients = Object.entries(clientRev).sort((a, b) => b[1] - a[1]).slice(0, 6);

  return { typesSorted, topClients };
}
